/**
 * Monaco Editor Setup - Unified Configuration
 * Uses Monaco's built-in formatting providers for JSON, CSS, HTML, and TypeScript
 * Custom languages (YAML, XML) with simplified formatting
 */

import * as monaco from 'monaco-editor'
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker'
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker'
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker'
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker'
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker'

const secretReferenceSchemaFileMatch = [
  'inmemory://config/*.json',
  'inmemory://config/*.jsonc',
]

const secretReferenceSchema = {
  uri: 'https://noetica.local/schemas/secret-reference.json',
  fileMatch: secretReferenceSchemaFileMatch,
  schema: {
    definitions: {
      vaultEntry: {
        type: 'object',
        properties: {
          provider: { type: 'string' },
          uri: { type: 'string' },
        },
        required: ['provider', 'uri'],
        additionalProperties: false,
      },
      secretInner: {
        type: 'object',
        properties: {
          vault: { type: 'string' },
          ref: { type: 'string' },
          version: { type: 'string' },
          _resolvedVaultUri: { type: 'string' },
        },
        required: ['ref'],
        additionalProperties: false,
      },
      secretWrapper: {
        type: 'object',
        properties: {
          $secret: { $ref: '#/definitions/secretInner' },
        },
        required: ['$secret'],
        additionalProperties: false,
      },
      configValue: {
        anyOf: [
          { $ref: '#/definitions/secretWrapper' },
          {
            type: 'object',
            additionalProperties: { $ref: '#/definitions/configValue' },
          },
          {
            type: 'array',
            items: { $ref: '#/definitions/configValue' },
          },
          { type: 'string' },
          { type: 'number' },
          { type: 'boolean' },
          { type: 'null' },
        ],
      },
    },
    type: 'object',
    properties: {
      _vaults: {
        type: 'object',
        additionalProperties: { $ref: '#/definitions/vaultEntry' },
      },
    },
    additionalProperties: { $ref: '#/definitions/configValue' },
  },
}

const sharedJsonDiagnosticsOptions = {
  validate: true,
  // Monaco exposes shared defaults here, so JSON and JSONC cannot be configured independently.
  allowComments: true,
  trailingCommas: 'ignore' as const,
  enableSchemaRequest: false,
  schemaValidation: 'warning' as const,
  schemas: [secretReferenceSchema],
}

// Configure Monaco workers for Vite environment - Non-blocking setup
self.MonacoEnvironment = {
  getWorker: function(_moduleId, label) {
    // Use asynchronous worker creation to prevent main thread blocking
    const createWorker = (WorkerClass: any) => {
      try {
        return new WorkerClass()
      } catch (error) {
        console.warn(`Failed to create worker for ${label}:`, error)
        // Fallback to editor worker
        return new editorWorker()
      }
    }

    switch (label) {
      case 'json':
        return createWorker(jsonWorker)
      case 'css':
      case 'scss':
      case 'less':
        return createWorker(cssWorker)
      case 'html':
      case 'handlebars':
      case 'razor':
        return createWorker(htmlWorker)
      case 'typescript':
      case 'javascript':
        return createWorker(tsWorker)
      default:
        return createWorker(editorWorker)
    }
  }
}

// Essential languages with enhanced support
export const SUPPORTED_LANGUAGES: Array<{
  id: string
  label: string
  extensions: string[]
  icon: string
}> = [
  { id: 'json', label: 'JSON', extensions: ['.json'], icon: 'json' },
  { id: 'jsonc', label: 'JSON with Comments', extensions: ['.jsonc'], icon: 'json' },
  { id: 'xml', label: 'XML', extensions: ['.xml', '.xsd', '.xsl', '.xslt'], icon: 'symbol-structure' },
  { id: 'javascript', label: 'JavaScript', extensions: ['.js'], icon: 'symbol-method' },
  { id: 'typescript', label: 'TypeScript', extensions: ['.ts'], icon: 'symbol-method' },
  { id: 'css', label: 'CSS', extensions: ['.css'], icon: 'symbol-color' },
  { id: 'html', label: 'HTML', extensions: ['.html', '.htm'], icon: 'symbol-structure' },
  { id: 'plaintext', label: 'Plain Text', extensions: ['.txt'], icon: 'symbol-text' }
]

// Enhanced XML formatter with better tag handling and attribute formatting
const xmlFormattingProvider: monaco.languages.DocumentFormattingEditProvider = {
  async provideDocumentFormattingEdits(model, options, _token) {
    const text = model.getValue()
    
    // Return early for empty or very large content to prevent blocking
    if (!text.trim() || text.length > 100000) {
      return []
    }
    
    return new Promise(resolve => {
      // Use requestAnimationFrame to prevent blocking
      requestAnimationFrame(() => {
        try {
          const indentSize = options.tabSize || 2
          const indentChar = options.insertSpaces ? ' ' : '\t'
          const indent = indentChar.repeat(indentSize)
          
          // Parse and format XML efficiently
          let formatted = text
            // Add newlines around tags
            .replace(/></g, '>\n<')
            // Remove leading/trailing whitespace from lines
            .replace(/^\s+|\s+$/gm, '')
            // Handle self-closing tags
            .replace(/(\s*)<(\w+)([^>]*?)\/>/g, (_match, _spaces, tagName, attributes) => {
              if (attributes.trim()) {
                return `<${tagName} ${attributes.trim()}/>`
              }
              return `<${tagName}/>`
            })
          
          const lines = formatted.split('\n').filter(line => line.trim())
          let indentLevel = 0
          
          const result = lines.map(line => {
            const trimmed = line.trim()
            if (!trimmed) return ''
            
            // Handle closing tags
            if (trimmed.startsWith('</')) {
              indentLevel = Math.max(0, indentLevel - 1)
            }
            
            const currentIndent = indent.repeat(indentLevel)
            
            // Handle opening tags (but not self-closing, comments, or declarations)
            if (trimmed.startsWith('<') && 
                !trimmed.startsWith('</') && 
                !trimmed.endsWith('/>') && 
                !trimmed.startsWith('<!--') &&
                !trimmed.startsWith('<?') &&
                !trimmed.startsWith('<!')) {
              
              // Check if it's a simple tag with content on the same line
              const tagMatch = trimmed.match(/^<([^>\s]+)[^>]*>(.+)<\/\1>$/)
              if (tagMatch) {
                // Single line tag with content - don't increase indent
                return currentIndent + trimmed
              } else {
                // Opening tag - increase indent for next line
                const result = currentIndent + trimmed
                indentLevel++
                return result
              }
            }
            
            return currentIndent + trimmed
          }).join('\n')
          
          resolve([{
            range: model.getFullModelRange(),
            text: result
          }])
        } catch (error) {
          console.warn('XML formatting failed:', error)
          resolve([])
        }
      })
    })
  }
}

// Configure Monaco Editor with built-in and custom language services
const configureLanguages = () => {
  console.log('🔧 Configuring Monaco Editor language services...')
  
  try {
    // Configure Monaco JSON/JSONC diagnostics once because jsonDefaults are shared.
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions(sharedJsonDiagnosticsOptions)
    
    // Enable JSON built-in formatting
    monaco.languages.json.jsonDefaults.setModeConfiguration({
      documentFormattingEdits: true,
      documentRangeFormattingEdits: true,
      completionItems: true,
      hovers: true,
      documentSymbols: true,
      tokens: true,
      colors: true,
      foldingRanges: true,
      diagnostics: true,
      selectionRanges: true
    })
    
    // Register XML language if not already registered
    try {
      monaco.languages.register({ id: 'xml' })
      console.log('✅ Registered XML language')
    } catch (e) {
      console.log('📝 XML language already registered')
    }
    
    // Register enhanced XML formatting provider
    monaco.languages.registerDocumentFormattingEditProvider('xml', xmlFormattingProvider)
    
    console.log('✅ Language services configured successfully')
    console.log('📝 Built-in formatting available for: JSON, JSONC, JavaScript, TypeScript, CSS, HTML')
    console.log('📝 Enhanced custom formatting available for: XML')
    
  } catch (error) {
    console.error('❌ Failed to configure language services:', error)
    throw error
  }
}

// Initialize Monaco Editor with lazy loading to prevent blocking
let isInitialized = false

export const initializeMonaco = async () => {
  if (isInitialized) return
  
  console.log('🚀 Initializing Monaco Editor...')
  
  try {
    // Use setTimeout to prevent blocking the main thread
    await new Promise(resolve => {
      setTimeout(() => {
        try {
          // Configure language services
          configureLanguages()
          isInitialized = true
          console.log('✅ Monaco Editor initialized successfully')
          resolve(void 0)
        } catch (error) {
          console.error('❌ Failed to configure language services:', error)
          resolve(void 0)
        }
      }, 0)
    })
    
  } catch (error) {
    console.error('❌ Failed to initialize Monaco Editor:', error)
    throw error
  }
}

// Optional cleanup function for disposing Monaco global state
export const disposeMonaco = () => {
  console.log('🧹 Disposing Monaco global state...')
  try {
    // Dispose all models to free memory
    monaco.editor.getModels().forEach(model => {
      try {
        model.dispose()
      } catch (error) {
        console.warn('Model disposal error:', error)
      }
    })
    
    // Reset initialization flag
    isInitialized = false
    console.log('✅ Monaco global state disposed')
  } catch (error) {
    console.warn('Monaco disposal error:', error)
  }
}

// Editor factory with proper model lifecycle management
export interface EditorOptions {
  container: HTMLElement
  language?: string
  value?: string
  readOnly?: boolean
  theme?: string
  fontSize?: number
  lineNumbers?: 'on' | 'off' | 'relative' | 'interval'
  minimap?: { enabled: boolean }
  scrollBeyondLastLine?: boolean
  wordWrap?: 'off' | 'on' | 'wordWrapColumn' | 'bounded'
  folding?: boolean
  renderLineHighlight?: 'none' | 'gutter' | 'line' | 'all'
  cursorBlinking?: 'blink' | 'smooth' | 'phase' | 'expand' | 'solid'
  automaticLayout?: boolean
}

export const createMonacoEditor = (options: EditorOptions) => {
  console.log('🔧 Creating Monaco Editor instance...')
  
  // Create a new model with a unique URI
  const modelUri = monaco.Uri.parse(`inmemory://model/${Date.now()}`)
  const model = monaco.editor.createModel(
    options.value || '', 
    options.language || 'plaintext', 
    modelUri
  )
  
  console.log(`🔧 Setting up language service for: ${options.language} at ${modelUri}`)
  console.log('🔍 Model details:', {
    language: model.getLanguageId(),
    uri: model.uri.toString(),
    modelValue: model.getValue().substring(0, 50)
  })
  console.log(`📝 Using built-in formatters for: ${options.language}`)
  console.log('📝 Available languages: JSON, JSONC, XML, JavaScript, TypeScript, CSS, HTML')
  
  // Create editor with model
  const editor = monaco.editor.create(options.container, {
    model,
    theme: options.theme || 'vs-dark',
    fontSize: options.fontSize || 14,
    lineNumbers: options.lineNumbers || 'on',
    minimap: options.minimap || { enabled: false },
    scrollBeyondLastLine: options.scrollBeyondLastLine ?? false,
    wordWrap: options.wordWrap || 'off',
    folding: options.folding ?? true,
    renderLineHighlight: options.renderLineHighlight || 'line',
    cursorBlinking: options.cursorBlinking || 'blink',
    automaticLayout: options.automaticLayout ?? true,
    readOnly: options.readOnly || false,
    
    // Enable formatting features
    formatOnPaste: true,
    formatOnType: true,
    
    // Editor behavior
    suggest: {
      showIcons: true,
      showSnippets: true
    },
    
    // Accessibility
    accessibilitySupport: 'auto'
  })
  
  console.log('Monaco Editor is ready:', editor)
  
  return { editor, model }
}

// Utility function to get language from filename
export const getLanguageFromFilename = (filename: string): string => {
  const ext = filename.toLowerCase().split('.').pop() || ''
  
  const languageMap: Record<string, string> = {
    'json': 'json',
    'jsonc': 'jsonc',
    'yml': 'yaml',
    'yaml': 'yaml',
    'xml': 'xml',
    'xsd': 'xml',
    'xsl': 'xml',
    'xslt': 'xml',
    'txt': 'plaintext',
    'md': 'markdown',
    'js': 'javascript',
    'ts': 'typescript',
    'jsx': 'typescript',
    'tsx': 'typescript',
    'css': 'css',
    'scss': 'scss',
    'less': 'less',
    'html': 'html',
    'htm': 'html',
    'py': 'python',
    'java': 'java',
    'c': 'c',
    'cpp': 'cpp',
    'cs': 'csharp',
    'php': 'php',
    'rb': 'ruby',
    'go': 'go',
    'rs': 'rust',
    'sh': 'shell',
    'bash': 'shell',
    'sql': 'sql'
  }
  
  return languageMap[ext] || 'plaintext'
}

// Export Monaco instance for external use
export { monaco }
